import { SettingsIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { Link, useNavigate } from "@tanstack/react-router";

import { cn } from "../../lib/utils";
import { PRAXA_CONTROL_SHELL_VERSION } from "../../praxaControlShell";
import {
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { SidebarProviderUpdatePill } from "./SidebarProviderUpdatePill";
import { SidebarUpdatePill } from "./SidebarUpdatePill";
import { PraxaSidebarAccountMenu } from "./PraxaWorkspaceAccountMenu";
import { PraxaHistoryNavigation } from "./PraxaHistoryNavigation";

export const SidebarChromeHeader = memo(function SidebarChromeHeader({
  isElectron,
}: {
  isElectron: boolean;
}) {
  return (
    <SidebarHeader
      className={cn(
        "@container/sidebar-header relative h-[var(--workspace-topbar-height)] shrink-0 flex-row items-center px-3 py-0 md:px-0",
        isElectron && "drag-region",
      )}
    >
      <SidebarTrigger className="relative z-10 md:hidden" />
      {isElectron ? null : <SidebarBrand />}
    </SidebarHeader>
  );
});

function SidebarBrand() {
  return (
    <Link
      aria-label="Go to threads"
      className="sidebar-brand relative z-10 ml-[var(--workspace-titlebar-content-left)] h-7 w-fit min-w-0 shrink-0 items-center gap-1 overflow-hidden rounded-md text-foreground outline-hidden ring-ring focus-visible:ring-2"
      to="/"
    >
      <PraxaWordmark />
    </Link>
  );
}

function PraxaWordmark() {
  return (
    <>
      <img
        alt=""
        aria-hidden="true"
        className="size-5 shrink-0 object-contain dark:hidden"
        src="/praxa-mark.png"
      />
      <img
        alt=""
        aria-hidden="true"
        className="hidden size-5 shrink-0 object-contain dark:block"
        src="/praxa-mark-light.png"
      />
    </>
  );
}

export const SidebarChromeNavigation = memo(function SidebarChromeNavigation() {
  return (
    <SidebarGroup
      className="px-2 pb-2 pt-1"
      data-praxa-control-navigation={PRAXA_CONTROL_SHELL_VERSION}
    >
      <PraxaHistoryNavigation />
    </SidebarGroup>
  );
});

export const SidebarChromeFooter = memo(function SidebarChromeFooter() {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const handleSettingsClick = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
    void navigate({ to: "/settings" });
  }, [isMobile, navigate, setOpenMobile]);

  return (
    <SidebarFooter className="gap-1 p-2">
      <SidebarProviderUpdatePill />
      <SidebarUpdatePill />
      <div
        className="relative min-h-10 [&_[data-sidebar-account-primary]]:w-[calc(100%-2.5rem)]"
        data-sidebar-account-footer=""
      >
        <PraxaSidebarAccountMenu />
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                aria-label="Settings"
                className="absolute right-0 top-1.5 text-sidebar-muted-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground focus-visible:ring-offset-sidebar"
                onClick={handleSettingsClick}
                size="icon-sm"
                variant="ghost"
              />
            }
          >
            <SettingsIcon className="size-4 shrink-0" />
          </TooltipTrigger>
          <TooltipPopup side="top">Settings</TooltipPopup>
        </Tooltip>
      </div>
    </SidebarFooter>
  );
});
